import FirebaseFirestore from 'firebase/firestore';
import { OrderType, PaymentType, DeliveryPartner } from '~/types/common';
import { TaxInfo, TaxType, CartItem, CartModifier, CartOption, PickMealPoint } from '~/types/cart';
import { CampaignCondition, CampaignType } from '~/types/coupon';

export enum OrderStatus {
  UNPAID = 'ORDER_STATUS_UNPAID',
  CREATED = 'ORDER_STATUS_CREATED',
  CONFIRMED = 'ORDER_STATUS_CONFIRMED',
  UNPROCESSED = 'ORDER_STATUS_UNPROCESSED',
  DISTRIBUTED = 'ORDER_STATUS_DISTRIBUTED',
  ACCEPTED = 'ORDER_STATUS_ACCEPTED',
  DRIVER_FOUND = 'ORDER_STATUS_DRIVER_FOUND',
  PICKED = 'ORDER_STATUS_PICKED',
  DELIVERED = 'ORDER_STATUS_DELIVERED',
  COMPLETED = 'ORDER_STATUS_COMPLETED',
  CANCELED = 'ORDER_STATUS_CANCELED',
  FAILED = 'ORDER_STATUS_FAILED',
  ABANDONED = 'ORDER_STATUS_ABANDONED',
  PAYMENT_FAILED = 'ORDER_STATUS_PAYMENT_FAILED',
  PAUSED = 'ORDER_STATUS_PAUSED',
  CHANGED = 'ORDER_STATUS_CHANGED',
}

export const failOrderStatus = [
  OrderStatus.CANCELED,
  OrderStatus.FAILED,
  OrderStatus.PAYMENT_FAILED,
  OrderStatus.ABANDONED,
];

export const endOfOrderStatus = [
  OrderStatus.CANCELED,
  OrderStatus.FAILED,
  OrderStatus.PAYMENT_FAILED,
  OrderStatus.ABANDONED,
  OrderStatus.COMPLETED,
];

export const inProgressStatus = [
  OrderStatus.DISTRIBUTED,
  OrderStatus.CREATED,
  OrderStatus.ACCEPTED,
  OrderStatus.PICKED,
  OrderStatus.DELIVERED,
  OrderStatus.UNPAID,
];

export enum LinePayStatus {
  PENDING = 'LINEPAY_STATUS_PENDING',
  CONFIRMED = 'LINEPAY_STATUS_CONFIRMED',
  REFUNDED = 'LINEPAY_STATUS_REFUNDED',
}

export enum UpayStatus {
  PENDING = 'UP_PAYMENT_PENDING',
  SUCCESS = 'UP_PAYMENT_SUCCESS',
  FAILED = 'UP_PAYMENT_FAILED',
  REFUNDING = 'UP_PAYMENT_REFUNDING',
  REFUNDED = 'UP_PAYMENT_REFUNDED',
}

export enum TzWalletStatus {
  SUCCESS = 'TZ_WALLET_PAYMENT_SUCCESS',
}

export enum DeliveryStatus {
  FINDING = 'DELIVERY_DRIVER_FINDING',
  GOING = 'DELIVERY_DRIVER_GOING',
  DELIVERING = 'DELIVERY_DRIVER_DELIVERING',
  COMPLETED = 'DELIVERY_DRIVER_COMPLETED',
}

export type CustomerLocation = {
  address: string;
  floor: string;
  geoPoint: FirebaseFirestore.GeoPoint;
};

export type Customer = {
  uid: Array<string>;
  name: string;
  phoneNumber: string;
  email: string;
};

export type PlatformInOrder = {
  id: string;
  name: string;
  location: {
    address: string;
    geoPoint: FirebaseFirestore.GeoPoint;
  };
  currency: string;
};

export type StoreInOrder = {
  id: string;
  name: string;
  location: {
    address: string;
    geopoint: FirebaseFirestore.GeoPoint;
  };
  currency: string;
};

export type OrderDelivery = {
  activeCarrier: DeliveryPartner;
  isSelfDelivery: boolean;
  note: string;
  carriers: {
    [carrier in DeliveryPartner]: {
      id: string;
      price: string;
      status: DeliveryStatus;
      driver: { id: string; name: string; phoneNumber: string };
    };
  };
  location: CustomerLocation;
};

export type ShippingFee = {
  store: number;
  customer: number;
};

export type ShippingFeeDetails = {
  store: { value: number; discount: number };
  customer: { value: number; discount: number };
};

export type OrderOption = CartOption & {
  // containers: Array<{ id: string; name: string; count: number }>;
};

export type OrderModifier = CartModifier;

export type OrderItem = CartItem & {
  // containers: Array<{ id: string; name: string; count: number }>;
};

export type OrderGroup = {
  memberId: string;
  memberName: string;
  items: Array<OrderItem>;
};

export type StoreOrderInPlatform = {
  storeId: string;
  orderId: string;
  name: string;
  items: Array<OrderItem>;
  groupItems: Array<OrderGroup>;
  status: OrderStatus;
  subtotal: number;
  total: number;
  tax: number;
};

export type LinePay = {
  orderId: string;
  transactionId: string;
  payInfo?: Array<{ amount: number; method: string; maskedCreditCardNumber?: string }>;
  paymentUrl: {
    app: string;
    web: string;
  };
};

export type CREDIT = { url: string; card: { expiration: string; last4Digits: string } };

// export type TZ_WALLET = {
//   transactionId: string;
//   maskedId: string;
//   balance: number;
// };

export type PaymentInfo = {
  createdAt: FirebaseFirestore.Timestamp;
  paidAt?: FirebaseFirestore.Timestamp;
  status: LinePayStatus | UpayStatus | TzWalletStatus;
  transactionId: string;
  LINEPAY?: LinePay;
  CREDIT?: CREDIT;
  // TZ_WALLET?: TZ_WALLET;
};

export type PlatformOrder = {
  clusterId: string | null;
  cartId: string;

  couponDetail?: { condition: CampaignCondition; type: CampaignType } | null;

  type: OrderType;
  isGroupOrder: boolean;
  orderNumber: string;

  delivery: OrderDelivery | null;
  pickMealPoint: PickMealPoint | null;
  shippingFee: ShippingFee;
  shippingFeeDetails: ShippingFeeDetails;

  orders: Array<StoreOrderInPlatform>;

  createdAt: FirebaseFirestore.Timestamp;
  pickMealAt: FirebaseFirestore.Timestamp;
  pickMealEnd: FirebaseFirestore.Timestamp | null;

  customer: Customer;

  paymentInfo?: PaymentInfo;
  paymentType: PaymentType;

  subtotal: number;
  tax: number;
  total: number;
  discount: number;

  status: OrderStatus;
  statusRecord: Partial<Record<OrderStatus, FirebaseFirestore.Timestamp>>;

  taxType: TaxType;
  taxInfo: TaxInfo;

  platform: PlatformInOrder;
  timezone: string;

  reasonsForCancellation?: Array<string>;
};

export type PlatformOrderWithId = PlatformOrder & { id: string };

export type StoreOrder = {
  platformOrderId: string | null;
  clusterId: string | null;
  cartId: string;

  type: OrderType;
  isGroupOrder: boolean;
  isLargeOrder: boolean;
  orderNumber: string;

  customer: Customer;
  delivery?: OrderDelivery;

  largeOrderPrepareMinutes: number;
  createdAt: FirebaseFirestore.Timestamp;
  pickMealAt: FirebaseFirestore.Timestamp;
  pickMealEnd: FirebaseFirestore.Timestamp | null;

  status: OrderStatus;
  statusRecord: Partial<Record<OrderStatus, FirebaseFirestore.Timestamp>>;

  platform?: PlatformInOrder;
  store: StoreInOrder;

  items: Array<OrderItem>; // isGroupOrder === false
  groupItems: Array<OrderGroup>; // isGroupOrder === true

  subtotal: number;
  tax: number;
  total: number;
  discount: number;

  paymentInfo?: PaymentInfo;
  paymentType: PaymentType;

  taxType: TaxType;
  taxInfo: TaxInfo;

  storeNote: string | null;

  preOrderRemindBeforeHours: number | null; //minutes, means turn status from CONFIRMED->UNPROCESSED when pickMealAt - preOrderRemindBefore
};
